import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trash2, Eye, TrendingUp, TrendingDown, Minus, Activity, Shield, AlertTriangle } from "lucide-react";
import { MarketSummary } from '@/services/MarketSummaryService';
import { useIsMobile } from '@/hooks/use-mobile';

interface MarketSummaryTableProps {
  summaries: MarketSummary[];
  onDelete: (id: string) => void;
  onView: (summary: MarketSummary) => void;
  isLoading: boolean;
}

export const MarketSummaryTable = ({ summaries, onDelete, onView, isLoading }: MarketSummaryTableProps) => {
  const isMobile = useIsMobile();

  const getSentimentBadge = (sentiment: string, confidence: number) => {
    switch (sentiment?.toLowerCase()) {
      case 'bullish':
        return (
          <Badge className="bg-green-500/20 text-green-700 border-green-500/30 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            Bullish ({confidence}%)
          </Badge>
        );
      case 'bearish':
        return (
          <Badge className="bg-red-500/20 text-red-700 border-red-500/30 flex items-center gap-1">
            <TrendingDown className="h-3 w-3" />
            Bearish ({confidence}%)
          </Badge>
        );
      case 'neutral':
        return (
          <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30 flex items-center gap-1">
            <Minus className="h-3 w-3" />
            Neutral ({confidence}%)
          </Badge>
        );
      default:
        return <Badge variant="outline">{sentiment}</Badge>;
    }
  };

  const getTrendConfluenceBadge = (confluence?: string) => {
    if (!confluence) return null;
    
    switch (confluence) {
      case 'STRONG':
        return (
          <Badge className="bg-green-600/20 text-green-800 border-green-600/30 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            Strong
          </Badge>
        );
      case 'PULLBACK':
        return (
          <Badge className="bg-blue-500/20 text-blue-700 border-blue-500/30 flex items-center gap-1">
            <Activity className="h-3 w-3" />
            Pullback
          </Badge>
        );
      case 'REVERSAL':
        return (
          <Badge className="bg-orange-500/20 text-orange-700 border-orange-500/30 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Reversal
          </Badge>
        );
      case 'MAJOR_REVERSAL':
        return (
          <Badge className="bg-red-600/20 text-red-800 border-red-600/30 flex items-center gap-1">
            <TrendingDown className="h-3 w-3" />
            Major Rev.
          </Badge>
        );
      default:
        return <Badge variant="outline">{confluence}</Badge>;
    }
  };

  const getMarketConditionBadge = (condition?: string) => {
    if (!condition) return null;
    
    switch (condition) {
      case 'STABLE':
        return (
          <Badge className="bg-green-500/20 text-green-700 border-green-500/30 flex items-center gap-1">
            <Shield className="h-3 w-3" />
            Stable
          </Badge>
        );
      case 'NORMAL':
        return (
          <Badge className="bg-blue-500/20 text-blue-700 border-blue-500/30 flex items-center gap-1">
            <Activity className="h-3 w-3" />
            Normal
          </Badge>
        );
      case 'VOLATILE':
        return (
          <Badge className="bg-red-500/20 text-red-700 border-red-500/30 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Volatile
          </Badge>
        );
      default:
        return <Badge variant="outline">{condition}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Market Analysis Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
            <span className="ml-2 text-sm sm:text-base">Loading results...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Mobile Card Component
  const MobileCard = ({ summary }: { summary: MarketSummary }) => (
    <Card className="mb-4">
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono text-xs">
              {summary.symbol}
            </Badge>
            <span className="text-sm font-medium">{summary.total_ideas} ideas</span>
          </div>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView(summary)}
              className="h-8 w-8 p-0"
            >
              <Eye className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(summary.id)}
              className="h-8 w-8 p-0"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Sentiment</span>
            {getSentimentBadge(summary.dominant_sentiment, summary.confidence_score)}
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Technical</span>
            {getTrendConfluenceBadge(summary.trend_confluence)}
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Market</span>
            {getMarketConditionBadge(summary.market_condition)}
          </div>
          
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Long: {summary.percent_long}%</span>
            <span>Short: {summary.percent_short}%</span>
          </div>
          
          <div className="text-xs text-muted-foreground">
            {new Date(summary.analysis_period_start).toLocaleDateString()} - {new Date(summary.analysis_period_end).toLocaleDateString()}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg sm:text-xl">Enhanced Market Analysis Results ({summaries.length})</CardTitle>
      </CardHeader>
      <CardContent className="p-3 sm:p-6">
        {summaries.length > 0 ? (
          <>
            {isMobile ? (
              // Mobile Cards Layout
              <div className="space-y-4">
                {summaries.map((summary) => (
                  <MobileCard key={summary.id} summary={summary} />
                ))}
              </div>
            ) : (
              // Desktop Table Layout with Enhanced Horizontal Scroll
              <ScrollArea className="w-full whitespace-nowrap rounded-md border" orientation="horizontal">
                <div className="min-w-[1300px] pb-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[120px] sticky left-0 bg-background z-10 border-r">Symbol</TableHead>
                        <TableHead className="min-w-[100px]">Ideas</TableHead>
                        <TableHead className="min-w-[150px]">Sentiment</TableHead>
                        <TableHead className="min-w-[120px]">Technical</TableHead>
                        <TableHead className="min-w-[100px]">Combined</TableHead>
                        <TableHead className="min-w-[120px]">Market</TableHead>
                        <TableHead className="min-w-[140px]">Period</TableHead>
                        <TableHead className="min-w-[160px] sticky right-0 bg-background z-10 border-l">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summaries.map((summary) => (
                        <TableRow key={summary.id}>
                          <TableCell className="min-w-[120px] sticky left-0 bg-background z-10 border-r">
                            <Badge variant="outline" className="font-mono text-xs">
                              {summary.symbol}
                            </Badge>
                            {summary.signals_analysis?.current_price && (
                              <div className="text-xs text-muted-foreground mt-1">
                                ${summary.signals_analysis.current_price.toLocaleString()}
                              </div>
                            )}
                          </TableCell>
                          
                          <TableCell className="min-w-[100px]">
                            <span className="font-medium">{summary.total_ideas}</span>
                            <div className="space-y-1 mt-1">
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-green-600">↗ {summary.num_long} ({summary.percent_long}%)</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-red-600">↘ {summary.num_short} ({summary.percent_short}%)</span>
                              </div>
                            </div>
                          </TableCell>
                          
                          <TableCell className="min-w-[150px]">
                            {getSentimentBadge(summary.dominant_sentiment, summary.confidence_score)}
                            <div className="mt-1">
                              {summary.top_keywords?.slice(0, 2).map((keyword, index) => (
                                <Badge key={index} variant="outline" className="text-xs mr-1">
                                  {keyword}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          
                          <TableCell className="min-w-[120px]">
                            <div className="space-y-1">
                              {getTrendConfluenceBadge(summary.trend_confluence)}
                              {summary.technical_confidence_score && (
                                <div className="text-xs text-muted-foreground">
                                  Technical: {summary.technical_confidence_score}%
                                </div>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell className="min-w-[100px]">
                            <div className="text-sm font-medium">
                              {summary.combined_confidence_score || summary.confidence_score}%
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Combined Score
                            </div>
                          </TableCell>
                          
                          <TableCell className="min-w-[120px]">
                            <div className="space-y-1">
                              {getMarketConditionBadge(summary.market_condition)}
                              {summary.support_levels && summary.resistance_levels && (
                                <div className="text-xs text-muted-foreground">
                                  S/R: {summary.support_levels.length}/{summary.resistance_levels.length}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell className="min-w-[140px] text-sm">
                            <div>
                              {new Date(summary.analysis_period_start).toLocaleDateString()}
                            </div>
                            <div className="text-muted-foreground">
                              to {new Date(summary.analysis_period_end).toLocaleDateString()}
                            </div>
                          </TableCell>
                          
                          <TableCell className="min-w-[160px] sticky right-0 bg-background z-10 border-l">
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onView(summary)}
                                className="h-8 px-3"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                                <span className="ml-1 hidden sm:inline">View</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onDelete(summary.id)}
                                className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span className="ml-1 hidden sm:inline">Delete</span>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm sm:text-base">No market analysis results found.</p>
            <p className="text-xs sm:text-sm mt-1">
              Run your first analysis to see results here.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
